import { useId } from 'react'

type FloodlightSvgProps = {
  active?: boolean
}

const LEFT_LED_COLUMNS = [17.5, 26, 34.5]
const RIGHT_LED_COLUMNS = [85.5, 94, 102.5]
const LED_ROWS = [30, 38.5, 47]

export const FloodlightSvg = ({ active = false }: FloodlightSvgProps) => {
  const uid = useId().replace(/:/g, '')
  const metalGradient = `${uid}-metal`
  const housingGradient = `${uid}-housing`
  const beamGradient = `${uid}-beam`
  const fixtureShadow = `${uid}-fixture-shadow`
  const ledGlow = `${uid}-led-glow`
  const groundBlur = `${uid}-ground-blur`

  const renderLeds = (columns: number[], prefix: string) =>
    columns.flatMap(cx =>
      LED_ROWS.map(cy => (
        <circle key={`${prefix}-${cx}-${cy}`} cx={cx} cy={cy} r="2.65" />
      ))
    )

  return (
    <svg
      viewBox="0 0 120 140"
      width="100%"
      height="100%"
      className={`floodlight-svg ${active ? 'floodlight-svg-active' : ''}`}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={metalGradient} x1="53" y1="0" x2="67" y2="0">
          <stop offset="0%" className="floodlight-metal-stop-dark" />
          <stop offset="48%" className="floodlight-metal-stop-light" />
          <stop offset="100%" className="floodlight-metal-stop-dark" />
        </linearGradient>
        <linearGradient id={housingGradient} x1="0" y1="22" x2="0" y2="55">
          <stop offset="0%" className="floodlight-housing-stop-top" />
          <stop offset="100%" className="floodlight-housing-stop-bottom" />
        </linearGradient>
        <linearGradient id={beamGradient} x1="60" y1="56" x2="60" y2="128">
          <stop offset="0%" className="floodlight-beam-stop-top" />
          <stop offset="72%" className="floodlight-beam-stop-mid" />
          <stop offset="100%" className="floodlight-beam-stop-bottom" />
        </linearGradient>
        <filter id={fixtureShadow} x="-30%" y="-30%" width="160%" height="180%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1.4" floodOpacity="0.24" />
        </filter>
        <filter id={ledGlow} x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="2" />
        </filter>
        <filter id={groundBlur} x="-40%" y="-150%" width="180%" height="400%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
      </defs>

      <g className="floodlight-beams" fill={`url(#${beamGradient})`}>
        <path d="M9 53h34l4 73H-3Z" />
        <path d="M77 53h34l12 73H73Z" />
      </g>

      <ellipse
        className="floodlight-ground-light"
        cx="60"
        cy="128"
        rx="32"
        ry="4"
        filter={`url(#${groundBlur})`}
      />
      <ellipse className="floodlight-ground-shadow" cx="60" cy="129" rx="24" ry="2.6" />

      <g className="floodlight-fixture" filter={`url(#${fixtureShadow})`}>
        <path className="floodlight-support-arm" d="M45 40h8M67 40h8" />

        <rect
          className="floodlight-center-mount"
          x="53"
          y="34.5"
          width="16"
          height="18"
          rx="2"
          fill={`url(#${metalGradient})`}
        />
        <rect
          className="floodlight-pole"
          x="58"
          y="51"
          width="5"
          height="72"
          rx="1.5"
          fill={`url(#${metalGradient})`}
        />

        <g className="floodlight-panel">
          <rect x="5" y="21" width="40" height="34" rx="4" fill={`url(#${housingGradient})`} />
          <rect className="floodlight-panel-face" x="10" y="24.5" width="30" height="27" rx="2.5" />
          <path className="floodlight-panel-highlight" d="M10 24h30" />
        </g>
        <g className="floodlight-panel">
          <rect x="75" y="21" width="40" height="34" rx="4" fill={`url(#${housingGradient})`} />
          <rect className="floodlight-panel-face" x="80" y="24.5" width="30" height="27" rx="2.5" />
          <path className="floodlight-panel-highlight" d="M80 24h30" />
        </g>

        <g className="floodlight-led-glow" filter={`url(#${ledGlow})`}>
          {renderLeds(LEFT_LED_COLUMNS, 'left-glow')}
          {renderLeds(RIGHT_LED_COLUMNS, 'right-glow')}
        </g>
        <g className="floodlight-leds">
          {renderLeds(LEFT_LED_COLUMNS, 'left-led')}
          {renderLeds(RIGHT_LED_COLUMNS, 'right-led')}
        </g>

        <rect
          className="floodlight-base-neck"
          x="53"
          y="120"
          width="14"
          height="6"
          rx="1.5"
          fill={`url(#${metalGradient})`}
        />
        <path className="floodlight-base" d="M49 125h22l5 6H44Z" fill={`url(#${metalGradient})`} />
        <path className="floodlight-base-edge" d="M43 131h34" />
      </g>
    </svg>
  )
}
